import { Text } from '@consta/uikit/Text'
import { selectProjectWellsVspTableData, useAppSelector } from '@core/core/store'
import type { Prettify } from 'common/types'
import type { FC, ReactNode } from 'react'
import { useState } from 'react'

import type { FilterForm, OnSetFilterFormFunc, RowWellVsp } from '../../types'
import { Filters } from '../Filters'
import { extractFilterFormContext, useStepController } from '../Navigation'
import type { WellsTableProps } from '../WellsTable'
import { WellsTable } from '../WellsTable'
import css from './TableWithFilter.css'

type TableWithFilterProps = Prettify<
  {
    prompt?: string
    forceWithVsp?: boolean
    saveContext?: OnSetFilterFormFunc
    additionalControls?: (rows: RowWellVsp[]) => ReactNode
  } & Omit<WellsTableProps, 'wellVsp' | 'isFieldEmpty'>
>

export const TableWithFilter: FC<TableWithFilterProps> = ({
  prompt,
  hasNextStep,
  forceWithVsp,
  saveContext,
  checkedWells,
  additionalControls,
  onRowClick,
  onChangeVspOffset,
  onCheck,
}) => {
  const { activeStep } = useStepController()
  const filterFormContext = extractFilterFormContext(activeStep)
  const [filterForm, setFilterForm] = useState<FilterForm>(
    filterFormContext ?? {
      fieldId: null,
      filter: 'all',
      searchQuery: null,
    },
  )

  const wellVspRows = useAppSelector<RowWellVsp[]>(state =>
    selectProjectWellsVspTableData(
      state,
      filterForm.filter,
      filterForm.searchQuery,
      filterForm.fieldId,
    ),
  )

  const onSetFilterForm: OnSetFilterFormFunc = form => {
    setFilterForm(form)
    saveContext?.(form)
  }

  const isFieldEmpty =
    wellVspRows.length === 0 && filterForm.filter === 'all' && filterForm.searchQuery?.length === 0

  return (
    <>
      <Filters
        initialForm={filterForm}
        forceWithVsp={forceWithVsp}
        onSetFilterForm={onSetFilterForm}
      />
      {!!prompt && (
        <Text className={css.prompt} size="xs" view="secondary">
          {prompt}
        </Text>
      )}
      {additionalControls?.(wellVspRows)}
      <div className={css.wrapper}>
        <WellsTable
          isFieldEmpty={isFieldEmpty}
          hasNextStep={hasNextStep}
          wellVsp={wellVspRows}
          checkedWells={checkedWells}
          onRowClick={onRowClick}
          onChangeVspOffset={onChangeVspOffset}
          onCheck={onCheck}
        />
      </div>
    </>
  )
}
